import React, { memo } from 'react';
import { useInstallPrompt } from '@/hooks/useInstallPrompt';

/**
 * InstallPrompt — PWA "Install Chrome Dino Tribute" banner
 *
 * Shown on the GameOverDashboard after the player has played >= 3 games.
 * Only appears when the browser has fired beforeinstallprompt (i.e., the
 * site is installable and hasn't been installed yet).
 *
 * Dismissible: hides for 7 days after the user clicks "Maybe later".
 * Non-intrusive: never shown during active gameplay, only on game over.
 */

interface InstallPromptProps {
  gamesPlayed: number;
}

export const InstallPrompt = memo(({ gamesPlayed }: InstallPromptProps) => {
  const { canInstall, promptInstall, dismissInstall } = useInstallPrompt();

  // Only show after 3+ games and when the browser says it's installable
  if (!canInstall || gamesPlayed < 3) return null;

  return (
    <div
      style={{
        marginTop: '10px',
        padding: '12px 14px',
        background: 'linear-gradient(135deg, #f0f9ff, #e0f2fe)',
        border: '1px solid #bae6fd',
        borderRadius: '10px',
        fontFamily: 'var(--font-ui, system-ui, -apple-system, Roboto, sans-serif)',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
      }}
      role="complementary"
      aria-label="Install Chrome Dino Game"
    >
      {/* App icon */}
      <div style={{
        width: '36px', height: '36px', borderRadius: '8px',
        background: 'var(--bg-color)', border: '1px solid #e0f2fe',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '20px', flexShrink: 0,
      }}>
        🦕
      </div>

      {/* Copy */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ fontSize: '12px', fontWeight: 700, color: '#0c4a6e', margin: '0 0 1px' }}>
          Install Chrome Dino Tribute
        </p>
        <p style={{ fontSize: '11px', color: '#0369a1', margin: 0 }}>
          Play offline, anytime — add to your home screen
        </p>
      </div>

      {/* CTAs */}
      <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
        <button
          onClick={promptInstall}
          style={{
            padding: '6px 12px',
            background: '#0284c7',
            color: '#ffffff',
            border: 'none',
            borderRadius: '6px',
            fontSize: '11px',
            fontWeight: 700,
            cursor: 'pointer',
            fontFamily: 'inherit',
            transition: 'background 0.15s ease',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = '#0369a1'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = '#0284c7'; }}
          aria-label="Install app"
        >
          Install
        </button>
        <button
          onClick={dismissInstall}
          style={{
            padding: '6px 8px',
            background: 'none',
            border: 'none',
            color: '#94a3b8',
            fontSize: '16px',
            cursor: 'pointer',
            lineHeight: 1,
            fontFamily: 'inherit',
          }}
          aria-label="Dismiss install prompt"
        >
          ×
        </button>
      </div>
    </div>
  );
});

InstallPrompt.displayName = 'InstallPrompt';
